import { Link } from "react-router-dom";
import { Gift, Share2, Sparkles, Users, Bell, ShieldCheck, ChevronDown } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useLang, LangToggle } from "@/lib/i18n";

const featureDefs = [
  { icon: Gift, key: "f1" }, { icon: Users, key: "f2" }, { icon: Sparkles, key: "f3" },
  { icon: Bell, key: "f4" }, { icon: Share2, key: "f5" }, { icon: ShieldCheck, key: "f6" },
];

const faqKeys = ["faq1", "faq2", "faq3", "faq4", "faq5"];

export default function Landing() {
  const { user } = useAuth();
  const { t } = useLang();
  const features = featureDefs.map((f) => ({ icon: f.icon, title: t(`${f.key}_t`), desc: t(`${f.key}_d`) }));
  const faqs = faqKeys.map((k) => ({ q: t(`${k}_q`), a: t(`${k}_a`) }));
  const steps = [
    { n: "1", title: t("step1_t"), desc: t("step1_d") },
    { n: "2", title: t("step2_t"), desc: t("step2_d") },
    { n: "3", title: t("step3_t"), desc: t("step3_d") },
  ];
  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-40 glass-nav border-b border-border/50">
        <div className="max-w-6xl mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-indigo-600 to-fuchsia-600 flex items-center justify-center shadow-[0_2px_10px_rgba(99,102,241,0.4)]">
              <Gift size={20} className="text-white" />
            </div>
            <span className="font-heading font-extrabold text-lg gradient-text">hediyeniiste</span>
          </div>
          <div className="flex items-center gap-2">
            <LangToggle />
            <Link to={user ? "/dashboard" : "/giris"} data-testid="landing-login-btn"
              className="px-5 py-2 rounded-full btn-gradient text-sm font-semibold active:scale-95">
              {user ? t("panel") : t("login")}
            </Link>
          </div>
        </div>
      </header>

      <section className="relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none select-none" aria-hidden="true">
          <div className="absolute inset-0 hero-grid" />
          <div className="absolute -top-32 -left-24 w-[480px] h-[480px] bg-indigo-500/20 rounded-full blur-[110px] animate-blob" />
          <div className="absolute top-24 -right-32 w-[460px] h-[460px] bg-fuchsia-500/15 rounded-full blur-[120px] animate-blob animation-delay-2000" />
          <div className="absolute bottom-0 left-1/3 w-[360px] h-[360px] bg-cyan-500/10 rounded-full blur-[100px] animate-blob animation-delay-4000" />
        </div>
        <div className="max-w-6xl mx-auto px-4 pt-16 pb-20 lg:pt-24 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div className="fade-up">
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-gradient-to-r from-indigo-500/15 via-violet-500/15 to-fuchsia-500/15 border border-indigo-400/30 backdrop-blur-xl text-xs font-bold tracking-[0.15em] uppercase mb-6 text-indigo-300">
              <span className="relative flex h-2 w-2 shrink-0">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-fuchsia-400 opacity-70" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-gradient-to-r from-indigo-400 to-fuchsia-400" />
              </span>
              {t("hero_badge")}
            </span>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.1] text-white">
              {t("hero_t1")} <span className="gradient-text drop-shadow-[0_0_30px_rgba(129,140,248,0.25)]">{t("hero_t2")}</span>
            </h1>
            <p className="text-base md:text-lg text-muted-foreground mt-6 max-w-lg">
              {t("hero_sub")}
            </p>
            <div className="flex flex-wrap gap-3 mt-8">
              <Link to={user ? "/dashboard" : "/giris"} data-testid="hero-cta-create"
                className="px-7 py-3.5 rounded-full btn-gradient font-semibold active:scale-95">
                {t("cta_create")}
              </Link>
              <a href="#nasil-calisir" data-testid="hero-cta-how"
                className="px-7 py-3.5 rounded-full bg-secondary/60 backdrop-blur-md border border-border/60 font-semibold active:scale-95 transition-all hover:border-indigo-400/50 flex items-center gap-2">
                {t("cta_how")} <ChevronDown size={16} />
              </a>
            </div>
          </div>
          <div className="relative fade-up" style={{ animationDelay: "0.15s" }}>
            <div className="absolute -inset-2 bg-gradient-to-r from-indigo-500/30 to-fuchsia-500/30 rounded-[2rem] blur-2xl opacity-60" aria-hidden="true" />
            <img src="https://images.unsplash.com/photo-1699729580048-61d3fe012225?crop=entropy&cs=srgb&fm=jpg&q=85&w=900"
              alt="hediyeniiste" className="relative rounded-3xl w-full object-cover aspect-[4/3] shadow-2xl border border-white/10" />
            <div className="absolute -bottom-5 -left-3 sm:-left-6 bg-card/80 backdrop-blur-xl rounded-2xl border border-border/50 shadow-xl p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-indigo-500/15 border border-indigo-500/30 flex items-center justify-center">
                <Gift size={18} className="text-indigo-400" />
              </div>
              <div>
                <p className="text-sm font-semibold">{t("hero_card_t")}</p>
                <p className="text-xs text-muted-foreground">{t("hero_card_d")}</p>
              </div>
            </div>
          </div>
        </div>
        </div>
      </section>

      <section id="nasil-calisir" className="bg-secondary/30 border-y border-border/30 py-20">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="font-heading text-2xl sm:text-3xl font-bold mb-12"><span className="gradient-text">{t("how_title")}</span></h2>
          <div className="grid md:grid-cols-3 gap-6">
            {steps.map((s, i) => (
              <div key={s.n} className="bg-card/60 backdrop-blur-md rounded-2xl p-8 border border-border/50 card-lift fade-up" style={{ animationDelay: `${i * 0.1}s` }}>
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-indigo-600 to-fuchsia-600 text-white font-heading font-bold text-xl flex items-center justify-center mb-5 shadow-[0_4px_14px_rgba(99,102,241,0.35)]">{s.n}</div>
                <h3 className="font-heading text-xl font-semibold mb-2">{s.title}</h3>
                <p className="text-muted-foreground text-sm">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-4 py-20">
        <h2 className="font-heading text-2xl sm:text-3xl font-bold mb-12"><span className="gradient-text">{t("why_title")}</span></h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {features.map(({ icon: Icon, title, desc }, i) => (
            <div key={title} className="bg-card/60 backdrop-blur-md rounded-2xl p-6 border border-border/50 card-lift fade-up" style={{ animationDelay: `${i * 0.05}s` }}>
              <div className="w-11 h-11 rounded-xl bg-indigo-500/15 border border-indigo-500/30 flex items-center justify-center mb-4">
                <Icon size={20} className="text-indigo-400" />
              </div>
              <h3 className="font-heading font-semibold mb-1.5">{title}</h3>
              <p className="text-sm text-muted-foreground">{desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="max-w-3xl mx-auto px-4 pb-20">
        <h2 className="font-heading text-2xl sm:text-3xl font-bold mb-8"><span className="gradient-text">{t("faq_title")}</span></h2>
        <Accordion type="single" collapsible className="space-y-3">
          {faqs.map((f) => (
            <AccordionItem key={f.q} value={f.q} className="bg-card/60 backdrop-blur-md rounded-2xl border border-border/50 px-6">
              <AccordionTrigger className="font-semibold text-left hover:no-underline">{f.q}</AccordionTrigger>
              <AccordionContent className="text-muted-foreground">{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      <section className="max-w-6xl mx-auto px-4 pb-20">
        <div className="relative overflow-hidden bg-card/60 backdrop-blur-xl border border-indigo-500/20 rounded-3xl p-10 sm:p-16 text-center">
          <div className="absolute -top-24 -left-16 w-72 h-72 bg-indigo-500/20 rounded-full blur-[100px] pointer-events-none" aria-hidden="true" />
          <div className="absolute -bottom-24 -right-16 w-72 h-72 bg-fuchsia-500/15 rounded-full blur-[100px] pointer-events-none" aria-hidden="true" />
          <h2 className="relative font-heading text-2xl sm:text-4xl font-extrabold text-white mb-4">{t("cta_banner_t")}</h2>
          <p className="relative text-muted-foreground mb-8 max-w-md mx-auto">{t("cta_banner_d")}</p>
          <Link to={user ? "/dashboard" : "/giris"} data-testid="footer-cta"
            className="relative inline-block px-8 py-3.5 rounded-full btn-gradient font-semibold active:scale-95">
            {t("cta_start")}
          </Link>
        </div>
      </section>

      <footer className="border-t border-border/50 py-8 text-center text-sm text-muted-foreground">
        <p>© 2026 hediyeniiste — {t("footer_tag")}</p>
        <div className="flex items-center justify-center gap-4 mt-1">
          <Link to="/iletisim" className="text-primary hover:underline" data-testid="footer-contact-link">{t("contact")}</Link>
          <a href="mailto:destek@hediyeniiste.com" className="text-primary hover:underline" data-testid="footer-support-email">
            destek@hediyeniiste.com
          </a>
        </div>
      </footer>
    </div>
  );
}
